import {
  channelSchema,
  channelWebhookSchema,
  conversationPageSchema,
  conversationSchema,
  inboundResultSchema,
  messagePageSchema,
  messageSchema,
  type ChannelView,
  type ChannelWebhookView,
  type ConversationPage,
  type ConversationQuery,
  type ConversationView,
  type CreateChatAppChannelInput,
  type CursorQuery,
  type InboundResult,
  type MessagePage,
  type MessageView,
  type SetHandoffInput,
  type SetOutcomeInput,
  type SimulateInboundInput,
  type UpdateConversationInput,
} from '@ai-sales/shared'
import { z } from 'zod'
import { apiRequest } from '../lib/api-client.js'

/** Диалоги, сообщения и действия оператора. */

export async function fetchConversations(
  workspaceId: string,
  query: Partial<ConversationQuery>,
): Promise<ConversationPage> {
  return apiRequest(`/workspaces/${workspaceId}/conversations`, conversationPageSchema, { query })
}

export async function fetchConversation(
  workspaceId: string,
  conversationId: string,
): Promise<ConversationView> {
  return apiRequest(
    `/workspaces/${workspaceId}/conversations/${conversationId}`,
    conversationSchema,
  )
}

export async function fetchMessages(
  workspaceId: string,
  conversationId: string,
  query: Partial<CursorQuery>,
): Promise<MessagePage> {
  return apiRequest(
    `/workspaces/${workspaceId}/conversations/${conversationId}/messages`,
    messagePageSchema,
    { query },
  )
}

export async function updateConversation(
  workspaceId: string,
  conversationId: string,
  input: UpdateConversationInput,
): Promise<ConversationView> {
  return apiRequest(
    `/workspaces/${workspaceId}/conversations/${conversationId}`,
    conversationSchema,
    { method: 'PATCH', body: input },
  )
}

export async function setHandoff(
  workspaceId: string,
  conversationId: string,
  input: SetHandoffInput,
): Promise<ConversationView> {
  return apiRequest(
    `/workspaces/${workspaceId}/conversations/${conversationId}/handoff`,
    conversationSchema,
    { method: 'PATCH', body: input },
  )
}

export async function setOutcome(
  workspaceId: string,
  conversationId: string,
  input: SetOutcomeInput,
): Promise<ConversationView> {
  return apiRequest(
    `/workspaces/${workspaceId}/conversations/${conversationId}/outcome`,
    conversationSchema,
    { method: 'PATCH', body: input },
  )
}

/**
 * Ручной ответ оператора.
 *
 * `autoPause` по умолчанию включён на сервере: написал клиенту — значит
 * вмешался, значит агент замолкает. Здесь это не передаётся отдельно
 * специально, чтобы в интерфейсе не появилось галочки «а можно и без
 * паузы»: такой выбор кончается двумя ответами клиенту на одно сообщение.
 */
export async function sendOperatorMessage(
  workspaceId: string,
  conversationId: string,
  text: string,
): Promise<MessageView[]> {
  return apiRequest(
    `/workspaces/${workspaceId}/conversations/${conversationId}/messages`,
    z.array(messageSchema),
    { method: 'POST', body: { text } },
  )
}

/** Подача сообщения «как от клиента» — только во внутренний тест-канал. */
export async function simulateInbound(
  workspaceId: string,
  channelId: string,
  input: SimulateInboundInput,
): Promise<InboundResult> {
  return apiRequest(
    `/workspaces/${workspaceId}/channels/${channelId}/inbound`,
    inboundResultSchema,
    { method: 'POST', body: input },
  )
}

// ─── каналы ChatApp ───

export async function createChatAppChannel(
  workspaceId: string,
  input: CreateChatAppChannelInput,
): Promise<ChannelView> {
  return apiRequest(`/workspaces/${workspaceId}/channels/chatapp`, channelSchema, {
    method: 'POST',
    body: input,
  })
}

export async function fetchChannelWebhook(
  workspaceId: string,
  channelId: string,
): Promise<ChannelWebhookView> {
  return apiRequest(
    `/workspaces/${workspaceId}/channels/${channelId}/webhook`,
    channelWebhookSchema,
  )
}

export async function registerChannelWebhook(
  workspaceId: string,
  channelId: string,
): Promise<ChannelWebhookView> {
  return apiRequest(
    `/workspaces/${workspaceId}/channels/${channelId}/webhook`,
    channelWebhookSchema,
    { method: 'POST' },
  )
}
